import { supabase } from '../../lib/supabase';
import words from 'an-array-of-english-words';
import {
  INVALID_WORD,
  CORRECT_ANSWER,
  INCORRECT_ANSWER,
  INCOMPLETE_ANSWER,
} from '../../constants/game';

const getNextLevel = async (childId, minSchoolYear = 1) => {
  // Call the database function that executes the SQL query
  const { data, error } = await supabase.rpc('get_next_learndle_word_level', {
    p_child_id: childId,
    p_child_school_year: minSchoolYear,
  });

  if (error) throw error;

  // RPC returns an array, get the first (and only) result
  const level = data && data.length > 0 ? data[0] : null;

  if (!level) return null;

  // Fetch all associated definitions for this word and attach them
  const { data: wordDefinitions, error: definitionsError } = await supabase
    .from('definitions')
    .select('id, definition, part, difficulty')
    .eq('word_id', level.word_id);

  if (definitionsError) throw definitionsError;

  return {
    ...level,
    word_definitions: wordDefinitions || [],
  };
};

const incrementAttempts = async ({ childId, levelId }) => {
  // First, try to fetch the existing progress record
  const { data: existingProgress, error: fetchError } = await supabase
    .from('progress')
    .select('attempts')
    .eq('child_id', childId)
    .eq('level_id', levelId)
    .maybeSingle();

  // If there's an error, throw it
  if (fetchError) {
    throw fetchError;
  }

  // Calculate the new attempts count
  const newAttempts = existingProgress ? existingProgress.attempts + 1 : 1;

  // Upsert the progress record with the incremented attempts
  const { error: upsertError } = await supabase.from('progress').upsert(
    [
      {
        child_id: childId,
        level_id: levelId,
        attempts: newAttempts,
      },
    ],
    { onConflict: ['child_id', 'level_id'] }
  );

  if (upsertError) throw upsertError;

  return newAttempts;
};

const incrementHintsUsed = async ({ childId, levelId }) => {
  // Try to fetch the existing progress record
  const { data: existingProgress, error: fetchError } = await supabase
    .from('progress')
    .select('hints_used')
    .eq('child_id', childId)
    .eq('level_id', levelId)
    .maybeSingle();

  if (fetchError) {
    throw fetchError;
  }

  const newHintsUsed = existingProgress
    ? (existingProgress.hints_used || 0) + 1
    : 1;

  // Upsert the progress record with the incremented hints_used
  const { error: upsertError } = await supabase.from('progress').upsert(
    [
      {
        child_id: childId,
        level_id: levelId,
        hints_used: newHintsUsed,
      },
    ],
    { onConflict: ['child_id', 'level_id'] }
  );

  if (upsertError) throw upsertError;

  return newHintsUsed;
};

const setLevelCompleted = async ({ childId, levelId }) => {
  const { error } = await supabase
    .from('progress')
    .update({
      level_status: 'completed',
      completed_at: new Date().toISOString(),
    })
    .eq('child_id', childId)
    .eq('level_id', levelId);

  if (error) throw error;
};

// Validate word against English dictionary
const isValidWord = (word) => {
  return typeof word === 'string' && words.includes(word.toLowerCase());
};

const checkAnswer = async ({ guess, targetWord, levelId, childId }) => {
  if (guess.length !== targetWord.length) {
    return {
      validAttempt: false,
      isCorrect: false,
      reason: INCOMPLETE_ANSWER,
    };
  }

  if (!isValidWord(guess)) {
    return {
      validAttempt: false,
      isCorrect: false,
      reason: INVALID_WORD,
    };
  }

  await incrementAttempts({ childId, levelId });

  if (guess.toLowerCase() === targetWord.toLowerCase()) {
    await setLevelCompleted({ childId, levelId });
    return {
      validAttempt: true,
      isCorrect: true,
      reason: CORRECT_ANSWER,
    };
  }

  return {
    validAttempt: true,
    isCorrect: false,
    reason: INCORRECT_ANSWER,
  };
};

export default {
  getNextLevel,
  incrementAttempts,
  incrementHintsUsed,
  setLevelCompleted,
  checkAnswer,
};
